import { parseAirshowBundleKey, parseAirshowExactExportOrderAttestation, type AirshowExactExportOrderAttestation } from "./airshowExactAttestation";

export type AirshowReviewLayout = "split" | "overlay";
export type AirshowAlignmentMode = "source-frame" | "bbox-center" | "manual";
export type AirshowGeometryMode = "exact-bezier" | "sampled-grid" | "tessellated" | "unavailable";
export type AirshowLoomFidelity = "exact-attested" | "exact-unattested" | "adapted" | "unavailable";
export type AirshowBoundingBox = readonly [
  readonly [number, number, number],
  readonly [number, number, number],
];
export interface AirshowSourceFrame {
  frame_id: "openvsp-source-native";
  axes: readonly ["x", "y", "z"];
  handedness: "right-handed";
  length_unit: "source-native-unbound";
}
export interface AirshowServerAlignment {
  mode: "identity-source-frame";
  candidate_to_reference: readonly number[];
}
export interface AirshowReviewRequest {
  schema: "loom.airshow-review-request/v1";
  entry_id: string;
  archive_member_path: string;
  source_sha256: string;
  resolution: number;
}
export interface AirshowReferenceAvailable {
  status: "available";
  representation: "historical-x3d-preview";
  trust: "historical-unattested";
  model_url: string;
  artifact_sha256: string;
  bbox: AirshowBoundingBox;
  vertices: number;
  triangles: number;
  shapes: number;
  warnings: readonly string[];
}
export interface AirshowReferenceUnavailable {
  status: "unavailable";
  representation: "unavailable";
  model_url: null;
  artifact_sha256: null;
  bbox: null;
  code: string;
  message: string;
  retryable: boolean;
  warnings: readonly string[];
}
export interface AirshowLoomAvailable {
  status: "available";
  engine: "loom-openvsp-adapter";
  representation: Exclude<AirshowGeometryMode, "unavailable">;
  display_representation: "tessellated";
  fidelity: Exclude<AirshowLoomFidelity, "unavailable">;
  model_url: string;
  bezier_url: string | null;
  metadata_url: string | null;
  bundle_key: string;
  geometry_complete: boolean;
  skipped_surfaces: readonly Readonly<Record<string, unknown>>[];
  export_order_attestation: AirshowExactExportOrderAttestation | null;
  bbox: AirshowBoundingBox | null;
  warnings: readonly string[];
}
export interface AirshowLoomUnavailable {
  status: "unavailable";
  representation: "unavailable";
  fidelity: "unavailable";
  model_url: null;
  bundle_key: null;
  geometry_complete: false;
  skipped_surfaces: readonly Readonly<Record<string, unknown>>[];
  export_order_attestation: null;
  bbox: null;
  code: string;
  message: string;
  retryable: boolean;
  warnings: readonly string[];
}
export interface AirshowReviewResponse {
  schema: "loom.airshow-review/v1";
  entry_id: string;
  archive_member_path: string;
  source_sha256: string;
  source_frame: AirshowSourceFrame;
  alignment: AirshowServerAlignment;
  reference: AirshowReferenceAvailable | AirshowReferenceUnavailable;
  loom: AirshowLoomAvailable | AirshowLoomUnavailable;
  quantitative_comparison_allowed: boolean;
}
export interface AirshowManualAlignment {
  translation: readonly [number, number, number];
  rotationDegrees: readonly [number, number, number];
  scale: number;
}
export const AIRSHOW_IDENTITY_ALIGNMENT: AirshowManualAlignment = {
  translation: [0, 0, 0],
  rotationDegrees: [0, 0, 0],
  scale: 1,
};
export interface AirshowCameraEvidence {
  signature: string;
  revision: number;
}
type UnknownRow = Record<string, unknown>;

function row(value: unknown, path: string): UnknownRow {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as UnknownRow;
}

function nonempty(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim() === "") throw new Error(`${path} must be a non-empty string`);
  return value;
}

function optionalText(value: unknown, fallback: string, path: string): string {
  return value === undefined || value === null ? fallback : nonempty(value, path);
}

function nonnegativeInteger(value: unknown, fallback: number, path: string): number {
  if (value === undefined) return fallback;
  if (!Number.isInteger(value) || (value as number) < 0) throw new Error(`${path} must be a nonnegative integer`);
  return value as number;
}

function sha256(value: unknown, path: string): string {
  const result = nonempty(value, path);
  if (!/^(?:sha256:)?[a-f0-9]{64}$/.test(result)) throw new Error(`${path} must be a SHA-256 identity`);
  return result;
}

function warnings(value: unknown, path: string): readonly string[] {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.some((warning) => typeof warning !== "string")) {
    throw new Error(`${path} must be a string array`);
  }
  return value;
}

function modelUrl(value: unknown, path: string): string {
  const result = nonempty(value, path);
  const pathname = result.split(/[?#]/, 1)[0] ?? "";
  if (!pathname.startsWith("/api/") || pathname.split("/").includes("..") || pathname.includes("\\")) {
    throw new Error(`${path} must be a same-origin /api artifact URL`);
  }
  return result;
}

function optionalModelUrl(value: unknown, path: string): string | null {
  return value === undefined || value === null ? null : modelUrl(value, path);
}

function bbox(value: unknown, path: string): AirshowBoundingBox {
  if (!Array.isArray(value) || value.length !== 2) throw new Error(`${path} must contain min and max corners`);
  const corners = value.map((corner, cornerIndex) => {
    if (!Array.isArray(corner) || corner.length !== 3
        || corner.some((coordinate) => typeof coordinate !== "number" || !Number.isFinite(coordinate))) {
      throw new Error(`${path}[${cornerIndex}] must contain three finite coordinates`);
    }
    return [corner[0] as number, corner[1] as number, corner[2] as number] as const;
  }) as [readonly [number, number, number], readonly [number, number, number]];
  if (corners[0].some((minimum, index) => minimum > corners[1][index]!)) {
    throw new Error(`${path} has inverted bounds`);
  }
  return corners;
}

function recordArray(value: unknown, path: string): readonly Readonly<Record<string, unknown>>[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) throw new Error(`${path} must be an array`);
  return value.map((item, index) => row(item, `${path}[${index}]`));
}

const IDENTITY_MATRIX = [
  1, 0, 0, 0, 0, 1, 0, 0,
  0, 0, 1, 0, 0, 0, 0, 1,
] as const;

function parseSourceFrame(value: unknown): AirshowSourceFrame {
  const source = row(value, "review.source_frame");
  if (source.frame_id !== "openvsp-source-native"
      || !Array.isArray(source.axes) || source.axes.join(",") !== "x,y,z"
      || source.handedness !== "right-handed"
      || source.length_unit !== "source-native-unbound") {
    throw new Error("review.source_frame is unsupported");
  }
  return {
    frame_id: "openvsp-source-native",
    axes: ["x", "y", "z"],
    handedness: "right-handed",
    length_unit: "source-native-unbound",
  };
}

function parseServerAlignment(value: unknown): AirshowServerAlignment {
  const source = row(value, "review.alignment");
  if (source.mode !== "identity-source-frame" || !Array.isArray(source.candidate_to_reference)
      || source.candidate_to_reference.length !== 16
      || source.candidate_to_reference.some((item, index) => item !== IDENTITY_MATRIX[index])) {
    throw new Error("review.alignment must be the identity source-frame matrix");
  }
  return { mode: "identity-source-frame", candidate_to_reference: source.candidate_to_reference };
}

function parseReference(value: unknown): AirshowReviewResponse["reference"] {
  const source = row(value, "review.reference");
  const sourceWarnings = warnings(source.warnings, "review.reference.warnings");
  if (source.status === "unavailable") {
    if (source.representation !== "unavailable") throw new Error("review.reference unavailable representation is invalid");
    const message = optionalText(source.message, "Historical OpenVSP reference is unavailable", "review.reference.message");
    return {
      status: "unavailable", representation: "unavailable", model_url: null,
      artifact_sha256: null, bbox: null,
      code: optionalText(source.code, "reference-unavailable", "review.reference.code"),
      message,
      retryable: source.retryable === true,
      warnings: sourceWarnings.length ? sourceWarnings : [message],
    };
  }
  if (source.status !== "available" || source.representation !== "historical-x3d-preview" || source.trust !== "historical-unattested") {
    throw new Error("review.reference status or representation is invalid");
  }
  return {
    status: "available",
    representation: "historical-x3d-preview",
    trust: "historical-unattested",
    model_url: modelUrl(source.model_url, "review.reference.model_url"),
    artifact_sha256: sha256(source.artifact_sha256, "review.reference.artifact_sha256"),
    bbox: bbox(source.bbox, "review.reference.bbox"),
    vertices: nonnegativeInteger(source.vertices, 0, "review.reference.vertices"),
    triangles: nonnegativeInteger(source.triangles, 0, "review.reference.triangles"),
    shapes: nonnegativeInteger(source.shapes, 0, "review.reference.shapes"),
    warnings: sourceWarnings,
  };
}

function parseLoom(value: unknown): AirshowReviewResponse["loom"] {
  const source = row(value, "review.loom");
  const sourceWarnings = warnings(source.warnings, "review.loom.warnings");
  const skippedSurfaces = recordArray(source.skipped_surfaces, "review.loom.skipped_surfaces");
  if (source.status === "unavailable") {
    if (source.representation !== "unavailable" || source.fidelity !== "unavailable") {
      throw new Error("review.loom unavailable fidelity is invalid");
    }
    const message = optionalText(source.message, "Loom OpenVSP adapter is unavailable", "review.loom.message");
    return {
      status: "unavailable", representation: "unavailable", fidelity: "unavailable",
      model_url: null, bundle_key: null, geometry_complete: false,
      skipped_surfaces: skippedSurfaces, export_order_attestation: null, bbox: null,
      code: optionalText(source.code, "loom-unavailable", "review.loom.code"),
      message,
      retryable: source.retryable === true,
      warnings: sourceWarnings.length ? sourceWarnings : [message],
    };
  }
  const representation = source.representation;
  const fidelity = source.fidelity;
  if (source.status !== "available"
      || !["exact-bezier", "sampled-grid", "tessellated"].includes(String(representation))
      || !["exact-attested", "exact-unattested", "adapted"].includes(String(fidelity))
      || source.engine !== "loom-openvsp-adapter" || source.display_representation !== "tessellated") {
    throw new Error("review.loom status, representation, or fidelity is invalid");
  }
  const geometryComplete = source.geometry_complete;
  if (typeof geometryComplete !== "boolean") throw new Error("review.loom.geometry_complete must be boolean");
  const attestation = parseAirshowExactExportOrderAttestation(source.export_order_attestation);
  if ((fidelity === "exact-attested" && (representation !== "exact-bezier" || !geometryComplete
      || skippedSurfaces.length > 0 || attestation === null))
      || (fidelity === "exact-unattested" && representation !== "exact-bezier")
      || (fidelity === "adapted" && representation === "exact-bezier")
      || (fidelity !== "exact-attested" && attestation !== null)) {
    throw new Error("review.loom fidelity contradicts its geometry evidence");
  }
  return {
    status: "available",
    engine: "loom-openvsp-adapter",
    representation: representation as AirshowLoomAvailable["representation"],
    display_representation: "tessellated",
    fidelity: fidelity as AirshowLoomAvailable["fidelity"],
    model_url: modelUrl(source.model_url, "review.loom.model_url"),
    bezier_url: optionalModelUrl(source.bezier_url, "review.loom.bezier_url"),
    metadata_url: optionalModelUrl(source.metadata_url, "review.loom.metadata_url"),
    bundle_key: parseAirshowBundleKey(source.bundle_key),
    geometry_complete: geometryComplete,
    skipped_surfaces: skippedSurfaces,
    export_order_attestation: attestation,
    bbox: source.bbox === undefined || source.bbox === null ? null : bbox(source.bbox, "review.loom.bbox"),
    warnings: sourceWarnings,
  };
}

export function parseAirshowReviewResponse(
  value: unknown,
  expected?: { entryId: string; archiveMemberPath: string; sourceSha256: string },
): AirshowReviewResponse {
  const source = row(value, "review");
  if (source.schema !== "loom.airshow-review/v1") throw new Error("review schema is unsupported");
  const entryId = nonempty(source.entry_id, "review.entry_id");
  const archiveMemberPath = nonempty(source.archive_member_path, "review.archive_member_path");
  const sourceSha256 = sha256(source.source_sha256, "review.source_sha256");
  if (expected && (entryId !== expected.entryId || archiveMemberPath !== expected.archiveMemberPath || sourceSha256 !== expected.sourceSha256)) {
    throw new Error("review entry identity does not match the selected Airshow model");
  }
  if (source.quantitative_comparison_allowed !== false) {
    throw new Error("review cannot allow quantitative comparison against an unattested historical reference");
  }
  return {
    schema: "loom.airshow-review/v1",
    entry_id: entryId,
    archive_member_path: archiveMemberPath,
    source_sha256: sourceSha256,
    source_frame: parseSourceFrame(source.source_frame),
    alignment: parseServerAlignment(source.alignment),
    reference: parseReference(source.reference),
    loom: parseLoom(source.loom),
    quantitative_comparison_allowed: false,
  };
}

function fixed(value: number): string {
  return (Object.is(value, -0) ? 0 : value).toFixed(3);
}

export function airshowAlignmentSignature(
  mode: AirshowAlignmentMode,
  manual: AirshowManualAlignment,
): string {
  return `${mode}|t=${manual.translation.map(fixed).join(",")}`
    + `|r=${manual.rotationDegrees.map(fixed).join(",")}|s=${fixed(manual.scale)}`;
}

export function airshowAlignmentMatrix(alignment: AirshowManualAlignment): readonly number[] {
  const [x, y, z] = alignment.rotationDegrees.map(THREE_DEGREES_TO_RADIANS);
  const cx = Math.cos(x!), sx = Math.sin(x!);
  const cy = Math.cos(y!), sy = Math.sin(y!);
  const cz = Math.cos(z!), sz = Math.sin(z!);
  const scale = alignment.scale;
  const [tx, ty, tz] = alignment.translation;
  return [
    scale * cy * cz, -scale * cy * sz, scale * sy, tx,
    scale * (sx * sy * cz + cx * sz), scale * (cx * cz - sx * sy * sz), -scale * sx * cy, ty,
    scale * (sx * sz - cx * sy * cz), scale * (cx * sy * sz + sx * cz), scale * cx * cy, tz,
    0, 0, 0, 1,
  ].map((value) => Object.is(value, -0) || Math.abs(value) < 1e-12 ? 0 : value);
}

const THREE_DEGREES_TO_RADIANS = (value: number): number => value * Math.PI / 180;
